import { useMemo } from 'react';
import convertEmployeesToEmployeeWithAssets from '@/helpers/convertEmployeesToEmployeeWithAssets';
import { deriveFilteredEmployees } from '@/helpers/deriveFilteredEmployees';
import { useEmployeesMap } from '@/hooks/useEmployeesMap';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import { useListAssetsV2Query } from '@/redux/slices/assetApiSlice';
import { useGetOrganizationEmployeesPaginatedQuery } from '@/redux/slices/organizationApiSlice';
import { useGetOrganizationPackagesQuery } from '@/redux/slices/packageApiSlice';
import { Asset, AssetListArgs } from '@/types/assets';
import { Employee, EmployeeWithAssets } from '@/types/employees';
import { GetOrganizationEmployeesArgs } from '@/types/organizations';
import {
  AssetStatus,
  ProductTypeName,
  SortOrder,
} from '@electricjs/core_entity-client';
import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { getEolDataForDevices } from '@/components/DeviceOverview/helpers/eolHelpers';
import { useGetOSEndOfLife } from '@/hooks/useGetOSEndOfLife';

export const findEmployeeWithAssets = (
  employees?: EmployeeWithAssets[],
  employeeId?: string
): EmployeeWithAssets | undefined => {
  return employees && employeeId
    ? employees.find(employee => employee.id === employeeId)
    : undefined;
};

export const ASSET_NOT_ARCHIVED_STATUSES: Set<AssetStatus> = new Set([
  AssetStatus.InUse,
  AssetStatus.Available,
  AssetStatus.UnderRepair,
  AssetStatus.InTransit,
  AssetStatus.WarehouseIntake,
]);

export const ASSET_UNAVAILABLE_STATUSES: Set<AssetStatus> = new Set([
  AssetStatus.Archived,
  AssetStatus.Donated,
  AssetStatus.Sold,
  AssetStatus.Disposed,
  AssetStatus.Missing,
]);

const NO_ASSETS: Asset[] = [];

const filterOutUnavailableAssets = (assets: Asset[]): Asset[] => {
  return assets.filter(asset => !ASSET_UNAVAILABLE_STATUSES.has(asset.status));
};

export type employeesWithAssetsResponse = {
  isFetching: boolean;
  isLoading: boolean;
  employeesWithAssets?: EmployeeWithAssets[];
  employees?: Employee[];
  total?: number;
  totalAssets?: number;
  assets?: Asset[];
  error?: SerializedError | FetchBaseQueryError;
};

export const useGetEmployeesWithAssets = ({
  devicesOnly = false,
  availableAssetsOnly = false,
  listAssetsQueryProps = {},
  listEmployeesQueryProps = {},
  skipApi = false,
  includeEolData = false,
}: {
  devicesOnly?: boolean;
  availableAssetsOnly?: boolean;
  listAssetsQueryProps?: Partial<Omit<AssetListArgs, 'organizationId'>>;
  listEmployeesQueryProps?: Partial<
    Omit<GetOrganizationEmployeesArgs, 'organizationId'>
  >;
  skipApi?: boolean;
  includeEolData?: boolean;
}): employeesWithAssetsResponse => {
  const organizationId = useGetOrganizationId();

  const skip = !organizationId || skipApi;

  // With the central map on, requests it can answer (status, term, dates,
  // paging, ...) take their employees from it instead of making another
  // request. Anything else, like a sort, keeps the server query.
  const employeesMap = useEmployeesMap();
  // Callers pass a new object every render, so memoize on its contents.
  const employeesArgsKey = JSON.stringify(listEmployeesQueryProps);
  const canDeriveFromMap = useMemo(
    () =>
      deriveFilteredEmployees([], JSON.parse(employeesArgsKey)) !== undefined,
    [employeesArgsKey]
  );
  const isFromCentralMap = !skip && employeesMap.isEnabled && canDeriveFromMap;
  const centralEmployees = useMemo(
    () =>
      isFromCentralMap && employeesMap.hasData
        ? deriveFilteredEmployees(
            employeesMap.employees,
            JSON.parse(employeesArgsKey)
          )
        : undefined,
    [
      isFromCentralMap,
      employeesMap.hasData,
      employeesArgsKey,
      employeesMap.employees,
    ]
  );

  const {
    data: employeesData,
    currentData: currentEmployeesData,
    isFetching: isFetchingEmployees,
    isLoading: isLoadingEmployees,
    error: employeesError,
  } = useGetOrganizationEmployeesPaginatedQuery(
    {
      organizationId,
      ...listEmployeesQueryProps,
    },
    {
      skip: skip || isFromCentralMap,
    }
  );

  const { currentData: currentEndOfLife, isFetching: isFetchingEndOfLife } =
    useGetOSEndOfLife({ skip: !includeEolData });

  const {
    currentData: currentOrganizationPackages,
    isFetching: isFetchingPackages,
    isLoading: isLoadingPackages,
    error: packagesError,
  } = useGetOrganizationPackagesQuery(organizationId ?? '', {
    skip,
  });

  const DEVICE_PRODUCT_TYPES = [
    ProductTypeName.Laptop,
    ProductTypeName.Desktop,
  ];

  const {
    data: assetsData,
    currentData: currentAssetsData,
    isFetching: isFetchingAssets,
    isLoading: isLoadingAssets,
    error: assetsError,
  } = useListAssetsV2Query(
    {
      organizationId,
      orderBy: 'name',
      sortOrder: SortOrder.Asc,
      ...(devicesOnly ? { productTypes: DEVICE_PRODUCT_TYPES } : {}),
      includeMdmStatus: true,
      ...listAssetsQueryProps,
    },
    { skip }
  );

  const assetsByStatus = useMemo(() => {
    const assetsWithEolData = getEolDataForDevices(
      assetsData?.results ?? NO_ASSETS,
      currentEndOfLife?.results ?? []
    );

    return availableAssetsOnly
      ? filterOutUnavailableAssets(assetsWithEolData)
      : assetsWithEolData;
  }, [assetsData?.results, currentEndOfLife?.results, availableAssetsOnly]);

  // The map only counts as fetching until it has data for the first time, so a
  // background refetch of it doesn't blank callers that gate on `isFetching`.
  const isWaitingForMap =
    isFromCentralMap &&
    (employeesMap.isLoading || !employeesMap.hasData) &&
    !employeesMap.error;
  const isFetching =
    (isFromCentralMap ? isWaitingForMap : isFetchingEmployees) ||
    isFetchingAssets ||
    isFetchingPackages ||
    isFetchingEndOfLife;
  const isLoading =
    isLoadingAssets ||
    (isFromCentralMap ? isWaitingForMap : isLoadingEmployees) ||
    isLoadingPackages;
  // A failed background refetch of the map keeps its last good list, so it is
  // only an error here while there is no list to show.
  const currentEmployeesError = isFromCentralMap
    ? employeesMap.hasData
      ? undefined
      : employeesMap.error
    : employeesError;
  const error = currentEmployeesError || assetsError || packagesError;

  // `data` keeps the previous arguments' result while new ones load, so build
  // only from `currentData`. A background refetch keeps the last good result.
  // The returned `employees`, `total` and `assets` still read `data`, so
  // callers see the same values as before during an arguments change.
  const currentEmployees = isFromCentralMap
    ? centralEmployees?.results
    : currentEmployeesData?.results;
  const currentAssets = currentAssetsData?.results;
  // EOL is skipped without devices permission, so it only blocks while it is
  // fetching for the current arguments for the first time.
  const isLoadingCurrentEndOfLife =
    isFetchingEndOfLife && currentEndOfLife === undefined;
  const isReady =
    !!currentEmployees &&
    !!currentAssets &&
    !!currentOrganizationPackages &&
    !isLoadingCurrentEndOfLife;

  const employeesWithAssets = useMemo(() => {
    if (!isReady) {
      return undefined;
    }

    const entitlementsByPackageId = new Map(
      currentOrganizationPackages.map(pkg => [pkg.id, pkg.package.entitlements])
    );

    // Map employees with their package entitlements
    const employeesWithEntitlements = currentEmployees.map(employee => ({
      ...employee,
      entitlements:
        (employee.organizationPackageId &&
          entitlementsByPackageId.get(employee.organizationPackageId)) ||
        [],
    }));

    return convertEmployeesToEmployeeWithAssets({
      employees: employeesWithEntitlements,
      assets: assetsByStatus,
    });
  }, [isReady, currentEmployees, currentOrganizationPackages, assetsByStatus]);

  return {
    employeesWithAssets,
    employees: isFromCentralMap
      ? centralEmployees?.results
      : employeesData?.results,
    total: isFromCentralMap ? centralEmployees?.total : employeesData?.total,
    assets: assetsByStatus,
    totalAssets: assetsData?.total,
    isFetching,
    isLoading,
    error: error as FetchBaseQueryError | SerializedError | undefined,
  };
};
